import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CycleDialog, { CycleFields } from "../../creacionCiclo/components/CycleDialog";
import { bogotaNow } from "../../creacionCiclo/model/cycleModel";
import { accesparkEntry, displayAccesparkDate, terminalDirection } from "../model/accesparkModel";
import useAccesparkForm from "../hooks/useAccesparkForm";
const terminalType = (terminal) => terminalDirection(terminal.type) || "Tipo desconocido";
export default function AccesparkForm({ onComplete, system }) {
  const form = useAccesparkForm(onComplete, system);
  const [confirmation, setConfirmation] = useState(null),
    [outcome, setOutcome] = useState(null);
  const prepare = (event) => {
    event.preventDefault();
    if (form.busy) return;
    const error = form.validate();
    form.setError(error);
    if (!error) {
      setOutcome(null);
      setConfirmation({ ...form.values, commerce: form.commerce, terminal: form.terminal });
    }
  };
  const confirm = async () => {
    const result = await form.submit();
    if (result?.success) setOutcome(result);
    else setConfirmation(null);
  };
  const close = () => {
    if (!form.busy) {
      setConfirmation(null);
      setOutcome(null);
    }
  };
  return (
    <Box
      component="form"
      onSubmit={prepare}
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: 52,
          borderRadius: 2,
          bgcolor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#91a6b8", borderWidth: "1.5px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#185a75" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#185a75",
            borderWidth: 2,
          },
          "&.Mui-disabled": { bgcolor: "#eef3f7" },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#c2cfd9" },
        },
        "& .MuiInputBase-input": { fontSize: ".95rem", fontWeight: 500, color: "#183b50" },
        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#61758a" },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          fontWeight: 600,
          color: "#405972",
          bgcolor: "#fff",
          px: 0.5,
        },
        "& .MuiInputLabel-root.Mui-disabled": { color: "#61758a" },
        "& .MuiInputLabel-root.Mui-focused": { color: "#185a75" },
        "& input::placeholder": { opacity: 1, color: "#61758a", fontWeight: 400 },
      }}
    >
      <Typography sx={{ fontWeight: 700, color: "#183b50", fontSize: "1rem" }}>
        Solicitar operación por terminal
      </Typography>
      <Typography sx={{ color: "#526579", fontSize: ".8rem", mb: 2 }}>
        El tipo de terminal identifica una entrada o una salida. Fecha y hora de Colombia (UTC−5).
      </Typography>
      {form.commerceError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button onClick={form.loadCommerces} disabled={form.loadingCommerces}>
              Reintentar
            </Button>
          }
        >
          {form.commerceError}
        </Alert>
      )}
      {form.terminalError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button onClick={form.retryTerminals} disabled={form.loadingTerminals}>
              Reintentar
            </Button>
          }
        >
          {form.terminalError}
        </Alert>
      )}
      {form.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {form.error}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            size="small"
            value={form.commerce}
            options={form.commerces}
            disabled={form.busy || form.loadingCommerces}
            loading={form.loadingCommerces}
            getOptionLabel={(item) => `${item.name} · ${item.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, item) => form.setField("commerce", item?.id || "")}
            loadingText="Cargando comercios..."
            noOptionsText="No se encontraron comercios"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Comercio"
                placeholder="Seleccione un comercio"
                InputLabelProps={{ shrink: true }}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            size="small"
            value={form.terminal}
            options={form.terminals}
            disabled={form.busy || form.loadingTerminals || !form.values.commerce}
            loading={form.loadingTerminals}
            getOptionLabel={(item) => `${item.name} · ${terminalType(item)} · #${item.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, item) => form.setField("terminal", item?.id || "")}
            loadingText="Cargando terminales..."
            noOptionsText="No hay terminales para este comercio"
            renderOption={({ key, ...optionProps }, item) => (
              <Box component="li" key={key} {...optionProps}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: ".8rem" }}>
                    {item.name} · #{item.id}
                  </Typography>
                  <Chip
                    size="small"
                    label={terminalType(item)}
                    sx={{
                      bgcolor: terminalDirection(item.type) === "Entrada" ? "#e5f3ef" : "#e8effa",
                      color: "#183b50",
                    }}
                  />
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Terminal"
                placeholder={
                  form.values.commerce
                    ? "Seleccione una terminal"
                    : "Seleccione primero un comercio"
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            )}
          />
        </Grid>
        {[
          ["plate", "Placa", "text"],
          ["date", "Fecha de operación", "date"],
          ["time", "Hora de operación", "time"],
        ].map(([name, label, type]) => (
          <Grid item xs={12} sm={4} key={name}>
            <TextField
              fullWidth
              size="small"
              required
              label={label}
              name={name}
              type={type}
              value={form.values[name]}
              disabled={form.busy}
              onChange={(event) => form.setField(name, event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={
                name === "time"
                  ? "Hora de Colombia (UTC−5)"
                  : name === "date"
                  ? "Día / mes / año"
                  : undefined
              }
              placeholder={name === "plate" ? "ABC123" : undefined}
              inputProps={
                name === "date"
                  ? { max: bogotaNow().date }
                  : name === "plate"
                  ? { maxLength: 8 }
                  : {}
              }
            />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={form.busy || form.loadingCommerces || form.loadingTerminals || !form.terminal}
          sx={{ bgcolor: "#185a75", color: "#fff", "&:hover": { bgcolor: "#103f54" } }}
        >
          Revisar operación
        </Button>
      </Box>
      <CycleDialog
        open={Boolean(confirmation)}
        compact
        busy={form.busy}
        onClose={close}
        title={
          outcome
            ? "Operación aceptada"
            : `Confirmar operación ${system === "ezytec" ? "Ezytec" : "Accespark"}`
        }
        subtitle="Revise el comercio, la terminal y la fecha antes de continuar"
        actions={
          <>
            {!outcome && (
              <Button variant="outlined" onClick={close} disabled={form.busy}>
                Volver
              </Button>
            )}
            <Button
              variant="contained"
              onClick={outcome ? close : confirm}
              disabled={form.busy}
              sx={{ bgcolor: "#185a75", color: "#fff" }}
            >
              {form.busy && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
              {outcome ? "Cerrar" : form.busy ? "Enviando..." : "Confirmar operación"}
            </Button>
          </>
        }
      >
        <Alert severity={outcome ? "success" : "info"}>
          {outcome
            ? "El servicio confirmó la operación."
            : "Se enviará la operación a la terminal seleccionada."}
        </Alert>
        {confirmation && (
          <CycleFields
            title="RESUMEN DE LA OPERACIÓN"
            fields={[
              ["Comercio", confirmation.commerce.name],
              ["Placa", confirmation.plate],
              ["Terminal", confirmation.terminal.name],
              ["Tipo de terminal", terminalType(confirmation.terminal)],
              ["ID de terminal", confirmation.terminal.id],
              [
                "Fecha · Colombia",
                displayAccesparkDate(accesparkEntry(confirmation.date, confirmation.time)),
              ],
              ...(outcome?.cycleId ? [["ID devuelto por el servicio", outcome.cycleId]] : []),
            ]}
          />
        )}
      </CycleDialog>
    </Box>
  );
}
AccesparkForm.propTypes = {
  onComplete: PropTypes.func,
  system: PropTypes.oneOf(["accespark", "ezytec"]),
};
AccesparkForm.defaultProps = { onComplete: null, system: "accespark" };
